import type { ComponentPropsWithoutRef } from "react";

import { cn } from "@/lib/utils";

// MUI's `CircularProgress`, indeterminate variant — the spinner the donor draws
// for every in-flight state it has: the shared `Button`'s `loadingIndicator`
// (`react-ui/src/button/button.tsx:23`), the variant tile
// (`item-variants-tiles.tsx:92-101`), the spec-usage cell (`SpecUsage.tsx:76`),
// the action menus, the paginated selects, the notification drawer — 40-odd call
// sites across `timmy/src` and `timmy/packages`.
//
// Ported as a **shared** Atlas component because it is shared in the donor
// (`docs/agents/migration-guidelines.md`, "Shared donor components port as
// shared Atlas components"). Nothing in `react-ui` or `timmy/src` registers a
// `MuiCircularProgress` `styleOverrides`, so MUI's own defaults *are* the source
// spec, and the values below are read out of the installed
// `@mui/material@9.2.0` rather than approximated.
//
// It replaces lucide's `Loader2`, which was never the same object: `Loader2` is
// a fixed three-quarter ring under a plain `rotate` keyframe, while MUI runs
// **two** animations at once — the root rotates (`circular-rotate`, 1.4s linear)
// and the circle's dash array breathes (`circular-dash`, 1.4s ease-in-out), so
// the arc's length grows and shrinks as it spins. A spinner that only rotates is
// the divergence, not a smaller version of it (owner ruling on #254's D6,
// 2026-09-02 — see #407).
//
// The geometry is MUI's `SIZE = 44` coordinate space (`CircularProgress.js:24`):
// a `22 22 44 44` viewBox holding one circle at `cx=cy=44`, radius
// `(44 - thickness) / 2`, so the stroke scales with the box instead of being a
// fixed pixel width. `size` only sets the root's CSS box; the drawing inside is
// resolution-independent.
//
// **The determinate variant** (`value` given) is the batch push overlay's
// (#1088, `PublishVersionNonLinearStepper.tsx:59-74`), its one donor consumer:
// MUI's branch (`CircularProgress.js:240-254`) — no rotation animation, the
// root turned `rotate(-90deg)` so the arc starts at twelve o'clock, the circle's
// dash the whole circumference `2π(44 - thickness)/2` offset by the part not
// done, `aria-valuenow`/`min`/`max` on the root, and the theme's standard
// transition (300ms, `cubic-bezier(0.4, 0, 0.2, 1)`) on the root's transform and
// the circle's dash offset, so the arc grows smoothly between polls.
//
// Still **not** ported: `disableShrink`, `enableTrackSlot` and `min`/`max`
// (always 0 and 100). No donor call site uses them, and inventing surface the
// source never shows is its own kind of divergence.

const SIZE = 44;

/** MUI's default `thickness` (`CircularProgress.js:209`). */
const DEFAULT_THICKNESS = 3.6;

export interface CircularProgressProps extends Omit<
  ComponentPropsWithoutRef<"span">,
  "color" | "children"
> {
  /**
   * The root box's edge, in px (or any CSS length as a string). MUI's default is
   * 40 (`CircularProgress.js:204`); the donor's own call sites override it —
   * 16 on the variant tile, 20 on the shared button, 24 in the paginated
   * selects, 64 in the publish stepper.
   */
  size?: number | string;
  /**
   * Stroke width **in viewBox units**, not px (`CircularProgress.js:210`).
   * The donor overrides it exactly once: `thickness={4}` at `size={64}`.
   */
  thickness?: number;
  /**
   * `"primary"` — MUI's default — paints the donor theme's `primary.main`
   * (`#003c77`, `react-ui/src/theme/theme.ts:10-13`). `"inherit"` takes the
   * surrounding text colour, which is what the shared donor `Button` asks for so
   * the indicator matches its label (`button.tsx:23`).
   */
  color?: "primary" | "inherit";
  /**
   * The determinate variant's value, 0–100. Absent is the indeterminate
   * spinner — the reason it has no default.
   */
  value?: number;
}

export function CircularProgress({
  size = 40,
  thickness = DEFAULT_THICKNESS,
  color = "primary",
  value,
  className,
  style,
  ...props
}: CircularProgressProps) {
  const determinate = value !== undefined;
  const radius = (SIZE - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  return (
    <span
      data-slot="circular-progress"
      // `aria-valuenow`/`min`/`max` only for the determinate variant
      // (`CircularProgress.js:252-254`): their absence is what makes a
      // progressbar read as indeterminate.
      role="progressbar"
      {...(determinate ? { "aria-valuenow": value, "aria-valuemin": 0, "aria-valuemax": 100 } : {})}
      className={cn(
        "inline-block",
        determinate
          ? "[transform:rotate(-90deg)] transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
          : "animate-mui-circular-rotate",
        color === "primary" && "text-lennar-blue-600",
        className,
      )}
      style={{ width: size, height: size, ...style }}
      {...props}
    >
      {/* `display: block` — "Keeps the progress centered" (`:127-131`).
          `size-full` is doing two jobs: it fills the root's box, and its
          `size-` prefix is what makes the shared button's
          `[&_svg:not([class*='size-'])]:size-4` rule skip this svg. Without it
          every in-button spinner is silently redrawn at 16px, anchored to the
          box's top-left instead of centred — 4px small and ~2px adrift of where
          the donor's 20px indicator sits. */}
      <svg className="block size-full" viewBox={`${SIZE / 2} ${SIZE / 2} ${SIZE} ${SIZE}`}>
        <circle
          className={
            determinate
              ? "stroke-current transition-[stroke-dashoffset] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
              : "animate-mui-circular-dash stroke-current [stroke-dasharray:80px,200px] [stroke-dashoffset:0]"
          }
          style={
            determinate
              ? {
                  strokeDasharray: circumference.toFixed(3),
                  strokeDashoffset: `${(((100 - value) / 100) * circumference).toFixed(3)}px`,
                }
              : undefined
          }
          cx={SIZE}
          cy={SIZE}
          r={radius}
          fill="none"
          strokeWidth={thickness}
        />
      </svg>
    </span>
  );
}
